import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { applyLayout } from "@/lib/appearance";
import { cn } from "@/lib/utils";

export function LayoutPanel({ isAdmin }) {
  const [presets, setPresets] = useState([]);
  const [current, setCurrent] = useState(null);        // resolved {tokens, preset_id, preset_name}
  const [tokens, setTokens] = useState({});
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try { setPresets(await apiFetch("/layout-presets")); } catch (e) { toast.error(e.message); }
    try {
      const a = await apiFetch("/appearance");
      setCurrent(a.layout || null); setTokens(a.layout?.tokens || {}); setDirty(false);
      if (a.layout) applyLayout(a.layout);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectPreset = async (id) => {
    if (!isAdmin) return toast.error("Only administrators can change organisation layout.");
    try {
      const res = await apiFetch("/appearance/layout", { method: "PUT", body: JSON.stringify({ layout_preset_id: id, layout_override: {} }) });
      setCurrent(res.layout); setTokens(res.layout.tokens || {}); setDirty(false); applyLayout(res.layout);
      toast.success("Layout updated for the organisation");
    } catch (e) { toast.error(e.message); }
  };
  const reset = async () => {
    try {
      const res = await apiFetch("/appearance/layout", { method: "PUT", body: JSON.stringify({ reset: true }) });
      setCurrent(res.layout); setTokens(res.layout.tokens || {}); setDirty(false); applyLayout(res.layout);
      toast.success("Reset to Comfortable");
    } catch (e) { toast.error(e.message); }
  };
  const setTok = (k, v) => { const nt = { ...tokens, [k]: v }; setTokens(nt); setDirty(true); applyLayout({ tokens: nt }); };
  const saveCustom = async () => {
    try {
      const res = await apiFetch("/appearance/layout", { method: "PUT", body: JSON.stringify({ layout_override: { tokens } }) });
      setCurrent(res.layout); setDirty(false); toast.success("Custom layout saved");
    } catch (e) { toast.error(e.message); }
  };

  const Ctrl = ({ label, k, min, max, step = 1, suffix = "px" }) => (
    <div>
      <div className="mb-1.5 flex items-center justify-between text-sm"><span>{label}</span>
        <span className="font-medium text-muted-foreground">{tokens[k]}{suffix}</span></div>
      <Slider min={min} max={max} step={step} value={[tokens[k] ?? min]} disabled={!isAdmin}
        onValueChange={([v]) => setTok(k, v)} data-testid={`layout-${k}`} />
    </div>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="layout-panel">
      <div className="space-y-6">
        <Card className="p-5 shadow-soft">
          <h3 className="mb-1 font-heading text-lg font-semibold">Density presets</h3>
          <p className="mb-4 text-xs text-muted-foreground">Visual density &amp; spacing only — navigation structure is unchanged. {isAdmin ? "" : "Managed by administrators."}</p>
          <div className="space-y-2">
            {presets.map((p) => {
              const active = current?.preset_id === p.id;
              return (
                <button key={p.id} disabled={!isAdmin} onClick={() => selectPreset(p.id)} data-testid={`layout-preset-${p.id}`}
                  aria-pressed={active} className={cn("flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors",
                    active ? "border-accent bg-accent-subtle" : "border-border hover:bg-muted/50", !isAdmin && "cursor-not-allowed opacity-70")}>
                  <div>
                    <div className="text-sm font-medium">{p.name} {p.is_preset && <span className="ml-1 text-[10px] uppercase text-muted-foreground">Built-in</span>}</div>
                    <div className="text-xs text-muted-foreground">{p.config?.tokens?.density} · radius {p.config?.tokens?.radius}px</div>
                  </div>
                  {active && <Check className="h-4 w-4 text-accent" />}
                </button>
              );
            })}
          </div>
          {isAdmin && <div className="mt-4 flex justify-end"><Button variant="outline" size="sm" className="gap-1.5" onClick={reset} data-testid="layout-reset"><RotateCcw className="h-4 w-4" /> Reset to Comfortable</Button></div>}
        </Card>

        {isAdmin && (
          <Card className="p-5 shadow-soft" data-testid="layout-custom">
            <h3 className="mb-3 font-heading text-lg font-semibold">Fine-tune (Custom)</h3>
            <div className="space-y-5">
              <Ctrl label="Page padding" k="page_px" min={12} max={56} step={2} />
              <Ctrl label="Corner radius" k="radius" min={0} max={24} step={1} />
              <Ctrl label="Card padding" k="card_pad" min={12} max={40} step={2} />
              <Ctrl label="Content max width" k="content_max" min={1080} max={1600} step={20} />
              <div>
                <div className="mb-1.5 text-sm">Table / list density</div>
                <Select value={tokens.density || "comfortable"} onValueChange={(v) => setTok("density", v)}>
                  <SelectTrigger data-testid="layout-density"><SelectValue /></SelectTrigger>
                  <SelectContent>{["compact", "comfortable", "spacious"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex justify-end"><Button size="sm" disabled={!dirty} onClick={saveCustom} data-testid="layout-save">{dirty ? "Save custom layout" : "Saved"}</Button></div>
            </div>
          </Card>
        )}
      </div>

      <Card className="p-6 shadow-soft" data-testid="layout-preview">
        <div className="mb-4 text-sm font-medium">Live preview</div>
        <div className="rounded-[var(--radius)] border border-border" style={{ padding: "var(--card-pad)" }}>
          <h3 className="font-heading text-xl font-semibold">Sample card</h3>
          <p className="text-sm text-muted-foreground" style={{ marginTop: "calc(var(--section-gap) / 3)" }}>Spacing, radius and density update live as you adjust the controls.</p>
          <div className="mt-4 flex gap-2"><Button size="sm">Action</Button><Button size="sm" variant="outline">Cancel</Button></div>
        </div>
        <div className="mt-4 overflow-hidden rounded-[var(--radius)] border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr><th className="px-3 text-left font-medium">Row</th><th className="px-3 text-left font-medium">Status</th></tr></thead>
            <tbody>
              {["Draft blog", "Festival plan", "Review"].map((r, i) => (
                <tr key={i} className="border-t border-border"><td className="px-3">{r}</td>
                  <td className="px-3"><span className="rounded bg-success/15 px-2 py-0.5 text-xs text-success">On track</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <span className="rounded bg-success/15 px-2 py-1 text-success">Success</span>
          <span className="rounded bg-warning/15 px-2 py-1 text-warning">Warning</span>
          <span className="rounded bg-info/15 px-2 py-1 text-info">Info</span>
          <span className="rounded bg-destructive/15 px-2 py-1 text-destructive">Error</span>
        </div>
      </Card>
    </div>
  );
}
